import React, {useState, useEffect} from 'react';
import Modal from '../common/Modal';
import Button from '../ui/Button';
import Loader from '../ui/Loader';
import axiosClient from '../../services/axiosClient';
import toast from 'react-hot-toast';
import {FaCheck, FaTimes} from 'react-icons/fa';

const TestQuestionsModal = ({
  isOpen,
  onClose,
  assessmentId,
  candidateId,
  testId,
}) => {
  const [loading, setLoading] = useState(true);
  const [testData, setTestData] = useState(null);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    if (isOpen && assessmentId && candidateId && testId) {
      fetchTestQuestions();
    }
  }, [isOpen, assessmentId, candidateId, testId]);

  const fetchTestQuestions = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get(
        `/assessment-test-sessions/questions/${assessmentId}/${candidateId}/${testId}`,
      );
      setQuestions(response.data.data.questions);
    } catch (error) {
      console.error('Error fetching test questions:', error);
      toast.error('Failed to fetch test questions');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 w-screen h-screen flex items-stretch justify-stretch bg-black bg-opacity-40">
      <div className="relative w-screen h-screen bg-white flex flex-col !max-w-none !max-h-none !rounded-none !m-0">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b">
          <h2 className="text-2xl font-bold">
            Test Questions - {testData?.subject || 'Test'}
          </h2>
          <button
            onClick={onClose}
            className="text-2xl text-gray-400 hover:text-red-500 font-bold"
          >
            &times;
          </button>
        </div>
        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto w-full">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Loader />
            </div>
          ) : (
            <div className="space-y-8 p-8 w-full max-w-5xl mx-auto">
              {questions.map((question, index) => (
                <div key={index} className="space-y-4">
                  {/* Question Header */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Question {index + 1} of {questions.length}
                    </span>
                  </div>
                  {/* Question Content */}
                  <div className="rounded-lg bg-gray-50 p-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {question.question}
                    </h3>
                  </div>
                  {/* Options */}
                  <div className="space-y-3">
                    {['optionA', 'optionB', 'optionC', 'optionD'].map(
                      (option) => {
                        const isCorrect = question.correctAnswer === option;
                        const isAttempted = question.attemptedOption === option;
                        const isCorrectAttempt = isCorrect && isAttempted;
                        const isIncorrectAttempt = !isCorrect && isAttempted;
                        return (
                          <div
                            key={option}
                            className={`rounded-md border p-4 ${
                              isCorrect
                                ? 'border-green-500 bg-green-50'
                                : isIncorrectAttempt
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div
                                  className={`mr-3 flex h-5 w-5 items-center justify-center rounded-full border ${
                                    isCorrect
                                      ? 'border-green-500 bg-green-500'
                                      : isAttempted
                                      ? 'border-blue-500 bg-blue-500'
                                      : 'border-gray-400'
                                  }`}
                                >
                                  {(isCorrect || isAttempted) && (
                                    <div className="h-2 w-2 rounded-full bg-white" />
                                  )}
                                </div>
                                <span>{question[option]}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {isCorrectAttempt && (
                                  <FaCheck className="h-4 w-4 text-green-600" />
                                )}
                                {isIncorrectAttempt && (
                                  <FaTimes className="h-4 w-4 text-red-600" />
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Modal Footer */}
        <div className="border-t bg-white p-6 flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default TestQuestionsModal;
